import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Box, Container, Flex, Text, Button, Textarea, Image, } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import BackNavigation from "../components/BackNavigation";
import { IMG_URL } from "../components/common/Constant";
import InteractiveStarRating from "../components/InteractiveStarRating";
import { useCustomer } from "../contexts/CustomerProvider";
import { useSubmitFeedback } from "../hooks/useSubmitFeedback";
import { useCustomToast } from "../components/useCustomToast";
import { usePubConfig } from "../hooks/usePubConfig";
function accountHomePathFromBase(accountBasePath) {
    if (accountBasePath === "/account" || accountBasePath.endsWith("/account")) {
        return accountBasePath;
    }
    return `${accountBasePath.replace(/\/$/, "")}/account`;
}
const FeedbackFormPage = ({ pubId, accountBasePath = "/account", }) => {
    var _a, _b, _c;
    const { customerData } = useCustomer();
    const navigate = useNavigate();
    const customToast = useCustomToast();
    const submitFeedback = useSubmitFeedback();
    const { data: pubConfig } = usePubConfig(pubId);
    const hasGoogleReviewUrl = !!((_c = (_b = (_a = pubConfig === null || pubConfig === void 0 ? void 0 : pubConfig.data) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.googleReviewUrl) === null || _c === void 0 ? void 0 : _c.toString().trim());
    const [rating, setRating] = useState(0);
    const [review, setReview] = useState("");
    useEffect(() => {
        if (!(customerData === null || customerData === void 0 ? void 0 : customerData.id)) {
            const currentPath = window.location.pathname + window.location.search;
            navigate(`/verify-phone/direct?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
        }
    }, [customerData, navigate]);
    const handleSubmit = async () => {
        var _a, _b, _c;
        if (!rating || !pubId) {
            customToast({ title: "Please select a rating", status: "error" });
            return;
        }
        if (rating <= 3 && !review.trim()) {
            customToast({
                title: "Please tell us more about your experience",
                status: "error",
            });
            return;
        }
        try {
            const result = await submitFeedback.mutateAsync({
                pubId: parseInt(pubId, 10),
                rating,
                review: review.trim() || undefined,
            });
            if ((result === null || result === void 0 ? void 0 : result.status_code) === "OK") {
                customToast({
                    title: "Thank you for your feedback!",
                    status: "success",
                });
                if (rating >= 4 && ((_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.googleReviewUrl)) {
                    window.open(result.data.googleReviewUrl, "_blank");
                }
                // Pop this route off the stack so we don't stack two /account entries (replace + /account
                // after /account → feedback caused a duplicate /account and "back" / first tap feeling dead).
                if (typeof window !== "undefined" && window.history.length > 1) {
                    navigate(-1);
                }
                else {
                    navigate(accountHomePathFromBase(accountBasePath), { replace: true });
                }
            }
            else {
                customToast({
                    title: (result === null || result === void 0 ? void 0 : result.status_message) || "Failed to submit feedback",
                    status: "error",
                });
            }
        }
        catch (err) {
            customToast({
                title: ((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
                    "Something went wrong. Please try again.",
                status: "error",
            });
        }
    };
    if (!(customerData === null || customerData === void 0 ? void 0 : customerData.id))
        return null;
    // When no Google Review URL, 4-5 stars behave like 1-3 (show Tell Us More, Submit Feedback)
    const showTellUsMore = rating > 0 && (rating <= 3 || !hasGoogleReviewUrl);
    const showGoogleReview = rating >= 4 && hasGoogleReviewUrl;
    const canShowSubmitButton = showGoogleReview || (showTellUsMore && review.trim().length > 0);
    const buttonLabel = showGoogleReview ? "Review Us On Google" : "Submit Feedback";
    const reviewPlaceholder = rating > 0 && rating <= 3
        ? `What could be better? Any specific dishes or staff members to mention?`
        : `What did you love? What could be better? Any specific\ndishes or staff members to mention?`;
    return (_jsxs(_Fragment, { children: [_jsx(BackNavigation, { heading: "" }), _jsx(Box, { bg: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(12px)", w: "100%", minH: "100dvh", h: "100dvh", overflow: "hidden", position: "relative", children: _jsx(Container, { w: ["100%", "100%", "100%", "400px"], h: "100dvh", background: "#111111", p: "0", overflowX: "hidden", overflowY: "auto", children: _jsxs(Box, { px: 6, pt: 24, pb: 24, children: [_jsxs(Flex, { alignItems: "center", justifyContent: "space-between", mb: 4, children: [_jsxs(Flex, { flexDirection: "column", flex: 1, minW: 0, children: [_jsx(Text, { fontFamily: "Glacial-Indifference-Bold", fontSize: "24px", color: "#FFFFFF", whiteSpace: "pre-line", children: `Share Your\nExperience` }), _jsx(Text, { fontFamily: "Glacial-Indifference", fontWeight: 400, fontSize: "12px", color: "#FFFFFF", whiteSpace: "pre-line", mt: 2, children: `Your honest feedback helps us and future\nguests. We read every review personally.` })] }), _jsx(Image, { src: `${IMG_URL}/icons/Feedback.svg`, alt: "Feedback", w: "120px", h: "120px", flexShrink: 0 })] }), _jsx(Text, { fontFamily: "Glacial-Indifference-Bold", fontSize: "12px", color: "#FFFFFF", mb: 3, children: "Overall Rating" }), _jsx(Box, { bg: "#292929", borderRadius: "10px", px: 4, py: 3, mb: 6, border: "1px solid", borderColor: "#494949", children: _jsx(InteractiveStarRating, { value: rating, onChange: setRating, size: "40px", filledColor: "#E3BD36", emptyColor: "#C6C6C6", emptyStrokeColor: "#C6C6C6", emptyStrokeWidth: "2" }) }), showTellUsMore && (_jsxs(_Fragment, { children: [_jsx(Text, { fontFamily: "Glacial-Indifference-Bold", fontSize: "12px", color: "#FFFFFF", mb: 3, children: "Tell Us More" }), _jsx(Textarea, { placeholder: reviewPlaceholder, value: review, onChange: (e) => setReview(e.target.value), bg: "#292929", border: "1px solid", borderColor: "#494949", borderRadius: "10px", fontFamily: "Glacial-Indifference", fontWeight: 400, fontSize: "12px", color: "#959595", _placeholder: {
                                            color: "#959595",
                                            fontFamily: "Glacial-Indifference",
                                            fontWeight: 400,
                                            fontSize: "12px",
                                        }, _hover: { borderColor: "#494949" }, _focus: { borderColor: "#494949", boxShadow: "none" }, rows: 4, resize: "vertical", mb: 6 })] }))] }) }) }), canShowSubmitButton && (_jsx(Box, { position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 20, display: "flex", justifyContent: "center", children: _jsx(Box, { w: ["100%", "100%", "100%", "400px"], bg: "#FFFFFF", pb: "env(safe-area-inset-bottom)", children: _jsx(Button, { mb: -1, w: "100%", h: "56px", bg: "#FFFFFF", fontFamily: "Glacial-Indifference-Bold", fontSize: "16px", color: "#000000", borderRadius: 0, border: "none", boxShadow: "none", _focus: { boxShadow: "none", outline: "none" }, onClick: handleSubmit, isLoading: submitFeedback.isLoading, isDisabled: !rating, _hover: { bg: "gray.100" }, children: buttonLabel }) }) }))] }));
};
export default FeedbackFormPage;
